
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Select, ofActionSuccessful } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, filter, map, of, merge, takeUntil, withLatestFrom, take } from 'rxjs';

import { ImportDataResultComponent } from '@app/pages/import-data-result/import-data-result.component';
import { PageBasedComponent } from '@app/pages/page.based.component';
import { LoadingSpinnerComponent } from '@app/shared/components/loading-spinner/loading-spinner.component';
import { MessageDataComponent } from '@app/shared/components/message-data/message-data.component';
import { DataGridComponent } from '@app/shared/components/smart-data-grid/data-grid/data-grid.component';
import { SmartSearchFormComponent } from '@app/shared/components/smart-search-form/smart-search-form.component';
import { SpinnerButtonComponent } from '@app/shared/components/spinner-button/spinner-button.component';
import { NgxsActionName, NgxsFormPath } from '@app/shared/constants';
import { SearchByItem, WebApiResponse, WebApiResponseBase } from '@app/shared/classes/general';
import { AppDataState } from '@app/shared/stores/app-data.state';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { DataGridColumnDef, GeExternalReportSearchByList } from './external-report.constant';
import { ReportLink } from '@app/shared/classes/report-link';
import { ReportLinkDataGridCell } from '@app/shared/components/smart-data-grid/data-grid-cell-templates/report-link-data-grid-cell.component';
import { CommonPageFilterSetting, CommonSearchCriteria } from '@app/shared/models/common-data.model';
import { DisableDeleteButtonDirective } from '@app/shared/directive/disable-delete-button.directive';
import { DisableEditButtonDirective } from '@app/shared/directive/disable-edit-button.directive';

@Component({
  selector: 'app-external-report',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingSpinnerComponent, DataGridComponent, MessageDataComponent, CommonModule, TranslateModule, DisableDeleteButtonDirective, DisableEditButtonDirective,
    SmartSearchFormComponent, SpinnerButtonComponent, ImportDataResultComponent, ReportLinkDataGridCell],
  templateUrl: './external-report.component.html',
  styleUrl: './external-report.component.css'
})
export class ExternalReportComponent extends PageBasedComponent {
  dataGridColumnDef = DataGridColumnDef;
  formPath = NgxsFormPath.EXTERNAL_REPORT_SEARCH_FORM;
  selectedItems: ReportLink[] = [];

  dataSource$: Observable<ReportLink[]>;
  externalReportSearchByList$: Observable<SearchByItem[]>;

  @Select(AppDataState.currentExternalReportSearchBy) currentExternalReportSearchBy$: Observable<CommonSearchCriteria[]>;
  @Select(AppDataState.currentPageFilterSetting(NgxsActionName.GET_EXTERNAL_REPORT)) currentPageFilterSetting$: Observable<CommonPageFilterSetting>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_EXTERNAL_REPORT)) searching$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_EXTERNAL_REPORT)) response$: Observable<WebApiResponse<ReportLink[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_EXTERNAL_REPORT)) errorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
    this.functionName = NgxsActionName.GET_EXTERNAL_REPORT;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.currentExternalReportSearchBy$.pipe(take(1)).subscribe(searchCriteria => this.defaultSearchCriteriaSub.next(searchCriteria));
    this.errorMessage$ = merge(this.commonDataLoadErrorMessage$, this.errorResponse$.pipe(map(errorResponse => this.getErrorMessages([errorResponse]))));

    this.dataSource$ = this.response$.pipe(filter(Boolean), map(c => c.data));
    this.totalCount$ = this.response$.pipe(filter(Boolean), map(c => c.totalCount));

    this.externalReportSearchByList$ = of(GeExternalReportSearchByList());
  }

  override onSearch = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetExternalReport());
  }


  onGetData = (info: CommonPageFilterSetting) => {
    info.functionName = NgxsActionName.GET_REPORT_LINK;
    this.store.dispatch(new AppDataAction.GetExternalReport(info));
  }
}
